import { useCallback } from 'react';
import axios from 'axios';
import { useMsal } from "@azure/msal-react";
import { silentRequest } from "../../authConfig";

// helper ร่วมของหน้าการตลาด (Funnel, สรุปรายวัน, ทะเบียนแคมเปญ, ค่าโฆษณา)

export const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL;

export const PLATFORM_LABELS = {
    'google-ads': 'Google Ads',
    'microsoft-ads': 'Microsoft Ads',
    'meta': 'Meta (Facebook / Instagram)',
    'tiktok': 'TikTok',
    'line': 'LINE',
    'other': 'อื่นๆ',
    'organic': 'ไม่ได้มาจากโฆษณา',
    'content': 'Content (ไม่ใช่โฆษณา)',
};

// ช่องทางของแคมเปญ content (ไม่ใช่โฆษณา) ตรงกับ CONTENT_CHANNELS ของ backend
export const CONTENT_CHANNEL_LABELS = {
    'blog': 'Blog บนเว็บไซต์',
    'facebook': 'Facebook',
    'instagram': 'Instagram',
    'youtube': 'YouTube',
    'tiktok': 'TikTok',
    'line': 'LINE',
    'linkedin': 'LinkedIn',
    'other': 'อื่นๆ',
};

export function channelsLabel(channels) {
    return (channels || []).map((channel) => CONTENT_CHANNEL_LABELS[channel] || channel).join(', ');
}

// แพลตฟอร์มที่กรอกค่าใช้จ่ายเองได้ (Google Ads ดึงจาก API)
export const MANUAL_PLATFORMS = ['microsoft-ads', 'meta', 'tiktok', 'line', 'other'];

export function platformLabel(platform) {
    return PLATFORM_LABELS[platform] || platform;
}

// เรียก API admin พร้อม access token: request('get', '/marketing/summary?from=..', body?)
export function useAdminApi() {
    const { instance } = useMsal();

    return useCallback((method, path, body) => {
        const account = instance.getAllAccounts()[0];

        return instance.acquireTokenSilent({ ...silentRequest, account: account }).then((tokenResponse) => axios({
            method,
            url: ADMIN_API_URL + path,
            data: body,
            headers: {
                'Authorization': 'Bearer ' + tokenResponse.accessToken
            }
        }));
    }, [instance]);
}

// ข้อความ error จาก API (422 มี message ภาษาไทยจาก backend)
export function apiErrorMessage(err, fallback) {
    const message = err && err.response && err.response.data && err.response.data.message;
    return message && err.response.status < 500 ? message : fallback;
}

export function toDateString(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

export function fromDateString(value) {
    return value ? new Date(value + 'T00:00:00') : null;
}

// วันที่แบบ พ.ศ. ตาม locale th-TH
export function formatDate(date) {
    return date ? date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

export function formatDay(value) {
    return value ? formatDate(fromDateString(value)) : '';
}

export function formatDateTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatNumber(value) {
    return Number(value || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 });
}

export function formatMoney(value) {
    return value === null || value === undefined ? '-' : Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatRoas(value) {
    return value === null || value === undefined ? '-' : Number(value).toFixed(2) + 'x';
}

export function percent(part, whole) {
    return whole > 0 ? (part / whole * 100).toFixed(1) + '%' : '-';
}

export function daysBefore(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
}

export function daysBetween(fromDate, toDate) {
    return Math.round((toDate - fromDate) / (24 * 60 * 60 * 1000)) + 1;
}

export function csvCell(value) {
    const text = value == null ? '' : String(value);
    return /[",\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
}

// ดาวน์โหลด CSV แบบ UTF-8 BOM ให้ Excel อ่านภาษาไทยได้
export function downloadCsv(filename, header, rows) {
    const lines = [header, ...rows].map((row) => row.map(csvCell).join(','));
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}

// แปลงข้อความ CSV เป็นแถว (รองรับ "..." และ "" ในช่อง)
export function parseCsv(text) {
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;

    const source = text.replace(/^﻿/, '');
    for (let i = 0; i < source.length; i++) {
        const char = source[i];
        if (quoted) {
            if (char === '"' && source[i + 1] === '"') { cell += '"'; i++; }
            else if (char === '"') quoted = false;
            else cell += char;
        } else if (char === '"') {
            quoted = true;
        } else if (char === ',') {
            row.push(cell); cell = '';
        } else if (char === '\n' || char === '\r') {
            if (char === '\r' && source[i + 1] === '\n') i++;
            row.push(cell); cell = '';
            if (row.some((value) => value.trim() !== '')) rows.push(row);
            row = [];
        } else {
            cell += char;
        }
    }
    row.push(cell);
    if (row.some((value) => value.trim() !== '')) rows.push(row);

    return rows.map((values) => values.map((value) => value.trim()));
}

export const homeStyles = {
    border: 0,
    margin: 0,
    width: '100%',
    background: '#FAF9F8'
};

export const sectionStyles = {
    root: {
        background: 'white',
        padding: 20,
        width: '100%',
        boxSizing: 'border-box',
    }
};

export const statStyles = {
    root: {
        background: 'white',
        borderLeft: '5px solid #0078d4',
        padding: '16px 20px',
        minWidth: 200,
        flex: 1,
    }
};

export const mutedText = { root: { color: '#605E5C' } };
